import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Shield, Award, CheckCircle, Users, Clock, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import { useLanguage } from '../context/LanguageContext';

const teamImages = [
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80',
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80',
];

const valueIcons = [Target, Users, Award, Clock];
const certIcons = [Shield, CheckCircle, Award, Target];

const NosotrosPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <CustomCursor />
      <Navbar />
      
      {/* Hero Section */}
      <HeroSection />
      
      {/* Our Story Section */}
      <StorySection />
      
      {/* Values Section */}
      <ValuesSection />
      
      {/* Team Section */}
      <TeamSection />
      
      {/* Certifications Section */}
      <CertificationsSection />
      
      {/* CTA Section */}
      <CTASection />
      
      <Footer />
    </div>
  );
};

const HeroSection = () => {
  const { t } = useLanguage();
  
  return (
    <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80"
          alt="Professional Construction Team"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center pt-32">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
        >
          {t('aboutPage.heroTagline')}
        </motion.span>
        
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-montserrat font-light text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.05em] uppercase mb-6"
        >
          {t('aboutPage.heroTitle1')}
          <br />
          <span className="text-[#C5A55C]">{t('aboutPage.heroTitle2')}</span> {t('aboutPage.heroTitleSuffix')}
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-raleway font-light text-lg md:text-xl text-white/70 max-w-3xl mx-auto mb-12 tracking-wide"
        >
          {t('aboutPage.heroDescription')}
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Link
            to="/contacto"
            className="group inline-flex items-center gap-3 px-10 py-4 bg-[#C5A55C] text-white hover:bg-white hover:text-[#1A1A1A] transition-all duration-500 font-montserrat font-medium text-sm tracking-[0.2em] uppercase"
            data-testid="hero-cta-contact"
          >
            {t('aboutPage.ctaButton')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-[1px] h-16 bg-gradient-to-b from-[#C5A55C] to-transparent" />
      </motion.div>
    </section>
  );
};

const StorySection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useLanguage();
  const storyPoints = t('aboutPage.storyPoints');
  
  return (
    <section ref={ref} className="py-32 bg-gradient-to-b from-[#0A0A0A] to-[#0F0F0F]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1 }}
            className="relative"
          >
            <div className="relative overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80"
                alt="Quality Craftsmanship"
                className="w-full h-[500px] object-cover"
              />
              {/* Gold frame accent */}
              <div className="absolute top-4 left-4 w-20 h-20 border-l-4 border-t-4 border-[#C5A55C]" />
              <div className="absolute bottom-4 right-4 w-20 h-20 border-r-4 border-b-4 border-[#C5A55C]" />
            </div>
            
            {/* Stats overlay */}
            <div className="absolute -bottom-8 -right-8 bg-[#C5A55C] p-8 hidden md:block">
              <span className="font-montserrat font-light text-5xl text-white block">15+</span>
              <span className="font-montserrat font-medium text-xs tracking-[0.2em] text-white/80 uppercase">{t('aboutPage.storyYears')}</span>
            </div>
          </motion.div>
          
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <span className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6">
              {t('aboutPage.storyTagline')}
            </span>
            <h2 className="font-montserrat font-light text-4xl md:text-5xl text-white tracking-[0.05em] uppercase mb-8">
              {t('aboutPage.storyTitle1')}
              <br />
              <span className="text-[#C5A55C]">{t('aboutPage.storyTitle2')}</span>
            </h2>
            <div className="w-16 h-[2px] bg-[#C5A55C] mb-8" />
            
            <div className="space-y-6">
              <p className="font-raleway font-light text-base text-white/70 leading-relaxed">
                <span className="text-[#C5A55C] font-medium">Martinez Solutions</span> {t('aboutPage.storyP1')}
              </p>
              
              <p className="font-raleway font-light text-base text-white/60 leading-relaxed">
                {t('aboutPage.storyP2')}
              </p>
              
              <p className="font-raleway font-light text-base text-white/60 leading-relaxed">
                {t('aboutPage.storyP3')}
              </p>
            </div>
            
            {/* Key points */}
            <div className="mt-10 grid grid-cols-2 gap-6">
              {storyPoints.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A55C]" />
                  <span className="font-raleway font-medium text-sm text-white/80">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ValuesSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useLanguage();
  const valuesData = t('aboutPage.values');
  
  const values = valuesData.map((v, i) => ({
    ...v,
    icon: valueIcons[i],
  }));
  
  return (
    <section ref={ref} className="py-32 bg-gradient-to-b from-[#F8F6F1] to-[#EFECE5] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      <div className="absolute top-12 left-12 w-16 h-16 border-l-2 border-t-2 border-[#C5A55C]/30" />
      <div className="absolute top-12 right-12 w-16 h-16 border-r-2 border-t-2 border-[#C5A55C]/30" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <span className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6">
            {t('aboutPage.valuesTagline')}
          </span>
          <h2 className="font-montserrat font-light text-4xl md:text-5xl text-[#1A1A1A] tracking-[0.08em] uppercase mb-6">
            {t('aboutPage.valuesTitle')}
          </h2>
          <div className="w-24 h-[2px] bg-[#C5A55C] mx-auto" />
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <ValueCard key={index} value={value} index={index} inView={inView} />
          ))}
        </div>
      </div>
      
      <div className="absolute bottom-12 left-12 w-16 h-16 border-l-2 border-b-2 border-[#C5A55C]/30" />
      <div className="absolute bottom-12 right-12 w-16 h-16 border-r-2 border-b-2 border-[#C5A55C]/30" />
    </section>
  );
};

const ValueCard = ({ value, index, inView }) => {
  const Icon = value.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.15 }}
      className="group p-8 bg-white shadow-lg hover:shadow-xl transition-all duration-500 text-center relative overflow-hidden border border-[#C5A55C]/10 hover:border-[#C5A55C]/40"
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-[#C5A55C]/10 to-transparent" />
      </div>
      
      <div className="w-16 h-16 mx-auto mb-6 bg-[#C5A55C]/10 group-hover:bg-[#C5A55C] flex items-center justify-center transition-all duration-300">
        <Icon className="w-7 h-7 text-[#C5A55C] group-hover:text-white stroke-[1.5] transition-colors duration-300" />
      </div>
      
      <h3 className="font-montserrat font-medium text-lg text-[#1A1A1A] mb-3 relative z-10">
        {value.title}
      </h3>
      <p className="font-raleway font-light text-sm text-[#1A1A1A]/60 leading-relaxed relative z-10">
        {value.description}
      </p>
      
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#C5A55C] scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
    </motion.div>
  );
};

const TeamSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useLanguage();
  const teamMembers = t('aboutPage.team');
  
  return (
    <section ref={ref} className="py-32 bg-[#1A1A1A] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <span className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6">
            {t('aboutPage.teamTagline')}
          </span>
          <h2 className="font-montserrat font-light text-4xl md:text-5xl text-white tracking-[0.08em] uppercase mb-6">
            {t('aboutPage.teamTitle')}
          </h2>
          <div className="w-24 h-[2px] bg-[#C5A55C] mx-auto" />
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {teamMembers.map((member, index) => (
            <TeamCard key={index} member={member} index={index} inView={inView} image={teamImages[index]} />
          ))}
        </div>
      </div>
    </section>
  );
};

const TeamCard = ({ member, index, inView, image }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.2 }}
      className="group relative overflow-hidden"
      data-testid={`team-card-${index}`}
    >
      {/* Image */}
      <div className="relative h-96 overflow-hidden bg-[#252525]">
        <img
          src={image}
          alt={member.name}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent" />
        
        {/* Hover overlay with description */}
        <div className="absolute inset-0 bg-[#C5A55C]/90 opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center p-8">
          <p className="font-raleway font-light text-base text-white text-center leading-relaxed">
            {member.description}
          </p>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6 bg-[#252525]">
        <span className="font-montserrat font-medium text-[10px] tracking-[0.25em] text-[#C5A55C] uppercase block mb-2">
          {member.role}
        </span>
        <h3 className="font-montserrat font-medium text-xl text-white">
          {member.name}
        </h3>
      </div>
      
      {/* Border accent */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#C5A55C] scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
    </motion.div>
  );
};

const CertificationsSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useLanguage();
  const certifications = t('aboutPage.certifications').map((cert, i) => ({
    ...cert,
    icon: certIcons[i],
  }));
  
  return (
    <section ref={ref} className="py-32 bg-gradient-to-b from-[#EFECE5] to-[#F8F6F1] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <span className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6">
            {t('aboutPage.certTagline')}
          </span>
          <h2 className="font-montserrat font-light text-4xl md:text-5xl text-[#1A1A1A] tracking-[0.08em] uppercase mb-6">
            {t('aboutPage.certTitle')}
          </h2>
          <div className="w-24 h-[2px] bg-[#C5A55C] mx-auto" />
        </motion.div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {certifications.map((cert, index) => (
            <CertCard key={index} cert={cert} index={index} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

const CertCard = ({ cert, index, inView }) => {
  const Icon = cert.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.15 }}
      className="group p-8 bg-white shadow-lg text-center border border-[#C5A55C]/20 hover:border-[#C5A55C]/50 transition-all duration-500"
    >
      <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#C5A55C] flex items-center justify-center group-hover:bg-[#C5A55C] transition-all duration-300">
        <Icon className="w-8 h-8 text-[#C5A55C] group-hover:text-white stroke-[1.5] transition-colors duration-300" />
      </div>
      
      <h3 className="font-montserrat font-medium text-lg text-[#1A1A1A] mb-2">
        {cert.title}
      </h3>
      <p className="font-raleway font-light text-sm text-[#1A1A1A]/60">
        {cert.description}
      </p>
    </motion.div>
  );
};

const CTASection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useLanguage();
  
  return (
    <section ref={ref} className="py-32 bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
        >
          <span className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6">
            {t('aboutPage.ctaTagline')}
          </span>
          <h2 className="font-montserrat font-light text-4xl md:text-5xl lg:text-6xl text-white tracking-[0.08em] uppercase mb-8">
            {t('aboutPage.ctaTitle1')}
            <br />
            <span className="text-[#C5A55C]">{t('aboutPage.ctaTitle2')}</span>
          </h2>
          <p className="font-raleway font-light text-lg text-white/60 max-w-2xl mx-auto mb-12">
            {t('aboutPage.ctaDescription')}
          </p>
          
          <Link
            to="/contacto"
            className="group inline-flex items-center gap-3 px-12 py-5 bg-[#C5A55C] text-white hover:bg-white hover:text-[#1A1A1A] transition-all duration-500 font-montserrat font-medium text-sm tracking-[0.2em] uppercase"
            data-testid="cta-request-consultation"
          >
            {t('aboutPage.ctaRequestButton')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default NosotrosPage;
